import React from 'react';
import {
  Box, Typography, Button, Chip, TextField, FormControl, InputLabel, Select, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  IconButton, CircularProgress, Pagination, Stack, InputAdornment, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Checkbox, ListItemText, TableSortLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import SyncIcon from '@mui/icons-material/Sync';
import { getRequest, postRequest } from 'Includes/api/apicall';
import { GET_URL, POST_URL } from 'Includes/urls';
import { TEAM_STATUS_CHOICES } from 'Includes/teamStatus';
import { useNavigate } from 'react-router-dom';
import axiosAPI from 'Includes/api/api';
import Swal from 'sweetalert2';

const STATUS_COLORS = {
  draft: { bg: '#f3f4f6', color: '#6b7280' },
  invoice_issued: { bg: '#dbeafe', color: '#2563eb' },
  partially_collected: { bg: '#fef3c7', color: '#d97706' },
  amount_collected: { bg: '#d1fae5', color: '#059669' },
  not_created: { bg: '#fee2e2', color: '#b91c1c' },
};

const STATUS_LABELS = {
  draft: 'Draft',
  invoice_issued: 'Issued',
  partially_collected: 'Partially Paid',
  amount_collected: 'Paid',
  not_created: 'Not Created',
};

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'invoice_issued', label: 'Issued' },
  { value: 'partially_collected', label: 'Partially Paid' },
  { value: 'amount_collected', label: 'Paid' },
];

const LIST_COLUMNS = [
  { key: 'order_number', label: 'Order #' },
  { key: 'collection_date', label: 'Collection Date', align: 'center' },
  { key: 'academic_year', label: 'Academic Year' },
  { key: 'company', label: 'Company' },
  { key: 'services', label: 'Services', align: 'center' },
  { key: 'total', label: 'Total', align: 'right' },
  { key: 'received', label: 'Received', align: 'right' },
  { key: 'balance', label: 'Balance', align: 'right' },
  { key: 'status', label: 'Status', align: 'center' },
  { key: 'assigned', label: 'Assigned', align: 'center' },
];

const formatAmount = (v) => {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function OrdersV2List(props) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(true);
  const [orders, setOrders] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [limit] = React.useState(20);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('');
  const [yearList, setYearList] = React.useState([]);
  const [selectedYear, setSelectedYear] = React.useState('');
  const [statusByTeamFilter, setStatusByTeamFilter] = React.useState('all');
  const [assigneeFilter, setAssigneeFilter] = React.useState('');
  const [showCollectionDates, setShowCollectionDates] = React.useState(false);
  const [upcomingCollectionCount, setUpcomingCollectionCount] = React.useState(0);
  const [serviceFilter, setServiceFilter] = React.useState([]);
  const [serviceTypes, setServiceTypes] = React.useState(['ERP', 'Website', 'Banner', 'Biometric', 'ID Cards']);
  const [syncing, setSyncing] = React.useState(false);
  const searchTimeoutRef = React.useRef(null);
  const [yearsReady, setYearsReady] = React.useState(false);
  const [collectionSummary, setCollectionSummary] = React.useState(null);
  const [sortBy, setSortBy] = React.useState('');
  const [sortDir, setSortDir] = React.useState('asc');
  const [updatingStatusId, setUpdatingStatusId] = React.useState(null);
  const [assigneeUsers, setAssigneeUsers] = React.useState([]);
  const [updatingAssigneeId, setUpdatingAssigneeId] = React.useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = React.useState([]);
  const [bulkAssignee, setBulkAssignee] = React.useState('');
  const [bulkAssigning, setBulkAssigning] = React.useState(false);

  React.useEffect(() => {
    fetchYears();
  }, []);

  React.useEffect(() => {
    getRequest(GET_URL.user.api, { status: 'active' }).then((response) => {
      if (response && response.status === 200) {
        const list = response.data?.data || response.data || [];
        setAssigneeUsers(Array.isArray(list) ? list : []);
      }
    });
  }, []);

  React.useEffect(() => {
    if (!yearsReady) return;
    fetchOrders();
  }, [page, statusFilter, selectedYear, statusByTeamFilter, serviceFilter, assigneeFilter, showCollectionDates, yearsReady, sortBy, sortDir]);

  React.useEffect(() => {
    setSelectedOrderIds([]);
  }, [page, statusFilter, selectedYear, statusByTeamFilter, serviceFilter, assigneeFilter, search]);

  const fetchYears = () => {
    getRequest(GET_URL.academicyear.api).then((response) => {
      if (response && response.status === 200) {
        const list = response.data.data || response.data || [];
        setYearList(list);
        const activeYear = list.find((y) => y.is_active || y.active);
        if (activeYear) {
          setSelectedYear(activeYear.id);
        } else if (list.length > 0) {
          setSelectedYear(list[0].id);
        }
        setYearsReady(true);
      }
    });
  };

  const fetchOrders = () => {
    setLoading(true);
    const params = {
      pageno: page,
      limit,
    };
    if (search) params.search = search;
    if (statusFilter) params.status = statusFilter;
    if (selectedYear) params.year = selectedYear;
    if (statusByTeamFilter && statusByTeamFilter !== 'all') {
      params.status_by_team = statusByTeamFilter;
    }
    if (Array.isArray(serviceFilter) && serviceFilter.length) {
      params.service = serviceFilter.join(',');
    }
    if (assigneeFilter) params.assigned_to = assigneeFilter;
    if (showCollectionDates) params.has_collection_date = 1;
    if (sortBy) {
      params.sort = sortBy;
      params.sort_dir = sortDir;
    }

    getRequest(GET_URL.orders_v2.api, params).then((response) => {
      if (response && response.status === 200) {
        setOrders(response.data.data || []);
        setTotal(response.data.total || 0);
        setCollectionSummary(response.data.collection_summary || null);
        setUpcomingCollectionCount(Number(response.data.upcoming_collection_count) || 0);
        const incomingTypes = Array.isArray(response.data.service_types) ? response.data.service_types : [];
        if (incomingTypes.length) {
          const merged = [];
          const seen = new Set();
          [...incomingTypes, ...serviceTypes].forEach((name) => {
            const label = (name || '').trim();
            const key = label.toLowerCase();
            if (!label || seen.has(key)) return;
            seen.add(key);
            merged.push(label);
          });
          setServiceTypes(merged);
        }
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  };

  const handleSort = (field) => {
    setPage(1);
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(field);
    setSortDir('asc');
  };

  const handleSearch = (val) => {
    setSearch(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
      fetchOrders();
    }, 500);
  };

  const handlePrintInvoice = async (order) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosAPI.get(
        GET_URL.orders_v2.api + order.id + '/invoice_pdf/',
        {
          responseType: 'blob',
          headers: { Authorization: `Token ${token}` },
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = window.URL.createObjectURL(blob);

      // Open print preview in new window
      const printWindow = window.open(fileURL, '_blank');
      if (printWindow) {
        printWindow.focus();
      }

      // Download file fallback
      const link = document.createElement('a');
      link.href = fileURL;
      link.setAttribute('download', `Proforma_Invoice_${order.invoice_number || order.order_number || order.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: 'success',
        title: 'Proforma Opened',
        text: 'Proforma opened in new tab for printing and downloaded.',
        timer: 1800,
        showConfirmButton: false,
      });
      fetchOrders();
    } catch (err) {
      console.error('Print proforma error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to generate proforma PDF' });
    }
  };

  const handleStatusChange = async (order, newStatus) => {
    if (!order?.id || !newStatus || newStatus === order.status) return;
    const previousStatus = order.status;
    setOrders((prev) => prev.map((row) => (row.id === order.id ? { ...row, status: newStatus } : row)));
    setUpdatingStatusId(order.id);
    try {
      const response = await postRequest(GET_URL.orders_v2.api + order.id + '/set_status/', { status: newStatus });
      if (response && (response.status === 200 || response.status === 201)) {
        const savedStatus = response.data?.status || newStatus;
        setOrders((prev) => prev.map((row) => (row.id === order.id ? { ...row, status: savedStatus } : row)));
      } else {
        setOrders((prev) => prev.map((row) => (row.id === order.id ? { ...row, status: previousStatus } : row)));
        Swal.fire({ icon: 'error', title: 'Could not update status' });
      }
    } catch (err) {
      setOrders((prev) => prev.map((row) => (row.id === order.id ? { ...row, status: previousStatus } : row)));
      Swal.fire({ icon: 'error', title: 'Could not update status', text: err?.response?.data?.error || '' });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleCollectionReminderChange = async (order, dateValue) => {
    if (!order?.id) return;
    const nextDate = dateValue || null;
    if ((order.collection_reminder_date || null) === nextDate) return;
    const previousDate = order.collection_reminder_date || null;
    setOrders((prev) => prev.map((row) => (
      row.id === order.id ? { ...row, collection_reminder_date: nextDate } : row
    )));
    try {
      const response = await postRequest(
        GET_URL.orders_v2.api + order.id + '/set_collection_reminder/',
        { collection_reminder_date: nextDate }
      );
      if (response && (response.status === 200 || response.status === 201)) {
        fetchOrders();
      } else {
        setOrders((prev) => prev.map((row) => (
          row.id === order.id ? { ...row, collection_reminder_date: previousDate } : row
        )));
        Swal.fire({ icon: 'error', title: 'Could not save collection date' });
      }
    } catch (err) {
      setOrders((prev) => prev.map((row) => (
        row.id === order.id ? { ...row, collection_reminder_date: previousDate } : row
      )));
      Swal.fire({ icon: 'error', title: 'Could not save collection date', text: err?.response?.data?.error || '' });
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedOrderIds.length || !bulkAssignee) return;
    setBulkAssigning(true);
    try {
      const response = await postRequest(GET_URL.orders_v2.api + 'bulk_assign/', {
        order_ids: selectedOrderIds,
        assigned_to: bulkAssignee,
      });
      if (response && (response.status === 200 || response.status === 201)) {
        const saved = response.data || {};
        const picked = assigneeUsers.find((user) => String(user.id) === String(saved.assigned_to || bulkAssignee));
        setOrders((prev) => prev.map((row) => (
          selectedOrderIds.includes(row.id)
            ? { ...row, assigned_to: saved.assigned_to || Number(bulkAssignee), assigned_to_name: saved.assigned_to_name || picked?.username || '' }
            : row
        )));
        setSelectedOrderIds([]);
        Swal.fire({
          icon: 'success',
          title: 'Orders assigned',
          text: `${saved.updated || selectedOrderIds.length} orders assigned to ${saved.assigned_to_name || picked?.username || 'staff'}.`,
          timer: 1600,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({ icon: 'error', title: 'Could not assign orders' });
      }
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not assign orders', text: err?.response?.data?.error || '' });
    } finally {
      setBulkAssigning(false);
    }
  };

  const handleAssigneeChange = async (order, userId) => {
    if (!order?.id) return;
    const nextId = userId === '' || userId == null ? null : Number(userId);
    const previousId = order.assigned_to || null;
    const previousName = order.assigned_to_name || '';
    if (String(previousId || '') === String(nextId || '')) return;
    const picked = assigneeUsers.find((user) => String(user.id) === String(nextId));
    setOrders((prev) => prev.map((row) => (
      row.id === order.id
        ? { ...row, assigned_to: nextId, assigned_to_name: picked?.username || '' }
        : row
    )));
    setUpdatingAssigneeId(order.id);
    try {
      const response = await postRequest(GET_URL.orders_v2.api + order.id + '/set_assignee/', { assigned_to: nextId });
      if (response && (response.status === 200 || response.status === 201)) {
        const saved = response.data || {};
        setOrders((prev) => prev.map((row) => (
          row.id === order.id
            ? { ...row, assigned_to: saved.assigned_to || null, assigned_to_name: saved.assigned_to_name || '' }
            : row
        )));
      } else {
        setOrders((prev) => prev.map((row) => (
          row.id === order.id ? { ...row, assigned_to: previousId, assigned_to_name: previousName } : row
        )));
        Swal.fire({ icon: 'error', title: 'Could not assign order' });
      }
    } catch (err) {
      setOrders((prev) => prev.map((row) => (
        row.id === order.id ? { ...row, assigned_to: previousId, assigned_to_name: previousName } : row
      )));
      Swal.fire({ icon: 'error', title: 'Could not assign order', text: err?.response?.data?.error || '' });
    } finally {
      setUpdatingAssigneeId(null);
    }
  };

  const handleDeleteOrder = async (order) => {
    const result = await Swal.fire({
      title: 'Delete Order?',
      text: `Are you sure you want to delete ${order.order_number || 'this order'}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete order',
    });

    if (result.isConfirmed) {
      try {
        await axiosAPI.delete(GET_URL.orders_v2.api + order.id + '/');
        Swal.fire({
          icon: 'success',
          title: 'Order Deleted',
          text: `${order.order_number || 'Order'} has been deleted.`,
          timer: 1800,
          showConfirmButton: false,
        });
        fetchOrders();
      } catch (err) {
        console.error('Delete order error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err?.response?.data?.error || 'Failed to delete order',
        });
      }
    }
  };

  const handleSyncCompanyOrders = async () => {
    if (!selectedYear) {
      Swal.fire({
        icon: 'warning',
        title: 'Academic Year Required',
        text: 'Please select an Academic Year from the filter dropdown before syncing company orders.',
        confirmButtonColor: '#2563eb',
      });
      return;
    }

    const yearObj = yearList.find((y) => String(y.id) === String(selectedYear));
    const yearName = yearObj ? yearObj.name : selectedYear;
    const result = await Swal.fire({
      title: `Sync Company Orders (${yearName})?`,
      text: `This will sync Company Orders ONLY for Academic Year "${yearName}". Other academic years will not be affected.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: `Yes, Sync for ${yearName}`,
      confirmButtonColor: '#2563eb',
      cancelButtonColor: '#64748b',
    });

    if (result.isConfirmed) {
      setSyncing(true);
      try {
        const payload = {
          year: selectedYear,
        };
        const response = await axiosAPI.post(POST_URL.orders_v2.api + 'copy_company_orders/', payload);
        Swal.fire({
          icon: 'success',
          title: 'Synced Successfully',
          text: `Company orders for Academic Year "${yearName}" synced successfully (Total: ${response.data.total_orders_v2 || 0}).`,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchOrders();
      } catch (err) {
        console.error('Sync error:', err);
        Swal.fire({
          icon: 'error',
          title: 'Sync Failed',
          text: err.response?.data?.error || 'Failed to sync company orders',
        });
      } finally {
        setSyncing(false);
      }
    }
  };

  const currentSelectedYearObj = yearList.find((y) => String(y.id) === String(selectedYear));
  const displayedOrders = React.useMemo(() => {
    if (!sortBy) return orders;
    const dir = sortDir === 'desc' ? -1 : 1;
    const statusLabels = STATUS_LABELS;
    const valueOf = (order) => {
      if (sortBy === 'order_number') return String(order.order_number || '').toLowerCase();
      if (sortBy === 'collection_date') return String(order.collection_reminder_date || '9999-12-31');
      if (sortBy === 'academic_year') return String(order.academic_year_name || '').toLowerCase();
      if (sortBy === 'company') return String(order.company_name || '').toLowerCase();
      if (sortBy === 'services') return Array.isArray(order.opted_features) ? order.opted_features.length : 0;
      if (sortBy === 'total') return parseFloat(order.total_amount || 0);
      if (sortBy === 'received') return parseFloat(order.received_amount || 0);
      if (sortBy === 'balance') return parseFloat(order.balance_amount || 0);
      if (sortBy === 'status') return String(statusLabels[order.status] || order.status || '').toLowerCase();
      if (sortBy === 'assigned') return String(order.assigned_to_name || '').toLowerCase();
      return '';
    };
    return [...orders].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return av > bv ? dir : -dir;
      }
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir;
    });
  }, [orders, sortBy, sortDir]);

  const selectableOrders = displayedOrders.filter((order) => order?.id && !order.is_not_created && order.status !== 'not_created');
  const selectableIds = selectableOrders.map((order) => order.id);
  const allPageSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedOrderIds.includes(id));

  const toggleOrderSelected = (orderId) => {
    setSelectedOrderIds((prev) => (
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    ));
  };

  const togglePageSelected = () => {
    setSelectedOrderIds((prev) => {
      if (allPageSelected) return prev.filter((id) => !selectableIds.includes(id));
      return Array.from(new Set([...prev, ...selectableIds]));
    });
  };

  const totalPages = Math.ceil(total / limit) || 1;
  const TEAM_STATUS_LABELS = {
    all: 'All clients',
    active: 'Active',
    low_engagement: 'Low Engagement',
    at_risk: 'At Risk',
    dead: 'Dead',
  };
  const filterLabels = [
    currentSelectedYearObj ? currentSelectedYearObj.name : (selectedYear ? 'Academic Year' : 'All years'),
    TEAM_STATUS_LABELS[statusByTeamFilter] || 'All clients',
    serviceFilter.length ? `Service: ${serviceFilter.join(', ')}` : 'All services',
    assigneeFilter === 'unassigned'
      ? 'Unassigned'
      : (assigneeFilter
        ? `Assignee: ${(assigneeUsers.find((user) => String(user.id) === String(assigneeFilter)) || {}).username || 'Staff'}`
        : 'All assignees'),
    statusFilter ? (STATUS_LABELS[statusFilter] || statusFilter) : 'All statuses',
    search ? `Search: "${search}"` : null,
  ].filter(Boolean);
  const isNotCreatedMode = statusFilter === 'not_created';
  const summary = collectionSummary || {
    order_count: 0,
    total_amount: 0,
    received_amount: 0,
    balance_amount: 0,
    by_status: [],
  };
  const summaryCards = [
    { key: 'total', label: 'Order Total', value: summary.total_amount, color: '#1e293b', bg: '#f8fafc', border: '#e2e8f0' },
    { key: 'received', label: 'Collected', value: summary.received_amount, color: '#15803d', bg: '#f0fdf4', border: '#bbf7d0' },
    { key: 'balance', label: 'Outstanding', value: summary.balance_amount, color: '#b91c1c', bg: '#fef2f2', border: '#fecaca' },
    {
      key: 'orders',
      label: isNotCreatedMode ? 'Clients' : 'Orders',
      value: isNotCreatedMode ? total : summary.order_count,
      color: '#1d4ed8',
      bg: '#eff6ff',
      border: '#bfdbfe',
      isCount: true,
    },
  ];

  const openCreateForCompany = (row) => {
    const qs = new URLSearchParams();
    if (row?.company) qs.set('company', String(row.company));
    if (selectedYear) qs.set('year', String(selectedYear));
    if (statusByTeamFilter && statusByTeamFilter !== 'all') {
      qs.set('status_by_team', statusByTeamFilter);
    }
    const suffix = qs.toString();
    navigate(suffix ? `/billing/orders-v2/form?${suffix}` : '/billing/orders-v2/form');
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
            Orders V2
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', fontSize: 13, mt: 0.2 }}>
            View and manage company orders, billing, and invoices
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
          <FormControl size="small" sx={{ minWidth: 160, bgcolor: '#fff' }}>
            <InputLabel>Academic Year</InputLabel>
            <Select
              value={selectedYear}
              label="Academic Year"
              onChange={(e) => { setSelectedYear(e.target.value); setPage(1); }}
            >
              <MenuItem value="">All</MenuItem>
              {yearList.map((yr) => (
                <MenuItem key={yr.id} value={yr.id}>{yr.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {!isNotCreatedMode && upcomingCollectionCount > 0 && (
            <Button
              variant={showCollectionDates ? 'contained' : 'outlined'}
              onClick={() => {
                const next = !showCollectionDates;
                setShowCollectionDates(next);
                setSortBy(next ? 'collection_date' : '');
                setSortDir('asc');
                setPage(1);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                whiteSpace: 'nowrap',
                borderColor: '#d97706',
                color: showCollectionDates ? '#fff' : '#b45309',
                bgcolor: showCollectionDates ? '#d97706' : '#fff',
                '&:hover': { borderColor: '#b45309', bgcolor: showCollectionDates ? '#b45309' : '#fffbeb' },
              }}
            >
              {upcomingCollectionCount} in next 5 days
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={handleSyncCompanyOrders}
            disabled={syncing}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              borderColor: '#cbd5e1',
              color: '#475569',
              px: 2,
              py: 0.8,
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            }}
          >
            {syncing ? 'Syncing...' : (currentSelectedYearObj ? `Sync Orders (${currentSelectedYearObj.name})` : 'Sync Company Orders')}
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/billing/orders-v2/id-cards')}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              borderColor: '#cbd5e1',
              color: '#334155',
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
            }}
          >
            ID Cards
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              const qs = new URLSearchParams();
              if (selectedYear) qs.set('year', String(selectedYear));
              if (statusByTeamFilter && statusByTeamFilter !== 'all') {
                qs.set('status_by_team', statusByTeamFilter);
              }
              try {
                sessionStorage.setItem('orders_v2_client_status', statusByTeamFilter || 'all');
              } catch (e) { /* ignore */ }
              const suffix = qs.toString();
              navigate(suffix ? `/billing/orders-v2/form?${suffix}` : '/billing/orders-v2/form');
            }}
            sx={{
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              px: 3,
              py: 0.9,
              boxShadow: '0 2px 8px rgba(37, 99, 235, 0.25)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1d4ed8 0%, #6d28d9 100%)',
              },
            }}
          >
            New Order
          </Button>
        </Stack>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
        <TextField
          size="small"
          placeholder="Search by order #, client name, or school code..."
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ flex: { xs: '1 1 100%', sm: '1 1 240px' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
          }}
        />
        <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' } }}>
          <InputLabel>Client Status (Team)</InputLabel>
          <Select
            value={statusByTeamFilter}
            label="Client Status (Team)"
            onChange={(e) => {
              setStatusByTeamFilter(e.target.value);
              setPage(1);
              try {
                sessionStorage.setItem('orders_v2_client_status', e.target.value || 'all');
              } catch (err) { /* ignore */ }
            }}
          >
            {TEAM_STATUS_CHOICES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 240px' } }}>
          <InputLabel>Service Type</InputLabel>
          <Select
            multiple
            value={serviceFilter}
            label="Service Type"
            onChange={(e) => {
              const next = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
              setServiceFilter(next);
              setPage(1);
            }}
            renderValue={(selected) => (selected.length ? selected.join(', ') : 'All')}
          >
            {serviceTypes.map((name) => (
              <MenuItem key={name} value={name}>
                <Checkbox checked={serviceFilter.indexOf(name) > -1} size="small" />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ flex: { xs: '1 1 100%', sm: '0 0 200px' } }}>
          <InputLabel>Assignee</InputLabel>
          <Select
            value={assigneeFilter}
            label="Assignee"
            onChange={(e) => {
              setAssigneeFilter(e.target.value);
              setPage(1);
            }}
          >
            <MenuItem value="">All assignees</MenuItem>
            <MenuItem value="unassigned">Unassigned</MenuItem>
            {assigneeUsers.map((user) => (
              <MenuItem key={user.id} value={String(user.id)}>
                {user.username || user.email || user.id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{
          display: 'flex',
          bgcolor: '#f1f5f9',
          borderRadius: 2.5,
          p: '3px',
          gap: '3px',
          flex: { xs: '1 1 100%', sm: '0 1 auto' },
          overflowX: 'auto',
        }}>
          {[
            { value: '', label: 'All', bg: '#fff', color: '#475569', activeBg: '#fff', activeColor: '#1e293b', activeShadow: '0 1px 3px rgba(0,0,0,0.1)' },
            { value: 'draft', label: 'Draft', bg: 'transparent', color: '#6b7280', activeBg: STATUS_COLORS.draft.bg, activeColor: STATUS_COLORS.draft.color },
            { value: 'invoice_issued', label: 'Issued', bg: 'transparent', color: '#6b7280', activeBg: STATUS_COLORS.invoice_issued.bg, activeColor: STATUS_COLORS.invoice_issued.color },
            { value: 'partially_collected', label: 'Partially Paid', bg: 'transparent', color: '#6b7280', activeBg: STATUS_COLORS.partially_collected.bg, activeColor: STATUS_COLORS.partially_collected.color },
            { value: 'amount_collected', label: 'Paid', bg: 'transparent', color: '#6b7280', activeBg: STATUS_COLORS.amount_collected.bg, activeColor: STATUS_COLORS.amount_collected.color },
            { value: 'not_created', label: 'Not Created', bg: 'transparent', color: '#6b7280', activeBg: STATUS_COLORS.not_created.bg, activeColor: STATUS_COLORS.not_created.color },
          ].map((btn) => {
            const isActive = statusFilter === btn.value;
            return (
              <Button
                key={btn.value}
                size="small"
                onClick={() => {
                  setStatusFilter(btn.value);
                  setPage(1);
                  if (btn.value === 'not_created' && serviceFilter.length) {
                    setServiceFilter([]);
                  }
                }}
                sx={{
                  minWidth: 0,
                  px: { xs: 1.5, sm: 2 },
                  py: 0.6,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 12,
                  whiteSpace: 'nowrap',
                  background: isActive ? btn.activeBg : btn.bg,
                  color: isActive ? btn.activeColor : btn.color,
                  boxShadow: isActive ? (btn.activeShadow || '0 1px 4px rgba(0,0,0,0.08)') : 'none',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: isActive ? btn.activeBg : '#e2e8f0',
                  },
                }}
              >
                {isActive && btn.value !== '' && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: btn.activeColor, mr: 0.8, flexShrink: 0 }} />
                )}
                {btn.label}
              </Button>
            );
          })}
        </Box>
        <Typography variant="body2" sx={{ color: '#64748b', ml: { xs: 0, sm: 'auto' }, fontWeight: 600 }}>
          {total} {isNotCreatedMode ? `client${total !== 1 ? 's' : ''}` : `order${total !== 1 ? 's' : ''}`}
        </Typography>
      </Paper>

      <Accordion
        defaultExpanded
        disableGutters
        sx={{
          mb: 3,
          borderRadius: 2.5,
          border: '1px solid #e2e8f0',
          boxShadow: 'none',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            px: 2,
            minHeight: 56,
            '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center', gap: 1, flexWrap: 'wrap' },
          }}
        >
          <Typography sx={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>
            Amount Collection
          </Typography>
          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
            {filterLabels.map((label) => (
              <Chip
                key={label}
                label={label}
                size="small"
                sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }}
              />
            ))}
          </Stack>
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, ml: { sm: 'auto' } }}>
            Collected {formatAmount(summary.received_amount)} · Outstanding {formatAmount(summary.balance_amount)}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5, mb: (summary.by_status || []).length ? 2 : 0 }}>
            {summaryCards.map((card) => (
              <Box
                key={card.key}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: card.bg,
                  border: `1px solid ${card.border}`,
                }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {card.label}
                </Typography>
                <Typography sx={{ mt: 0.5, fontWeight: 800, fontSize: 20, color: card.color }}>
                  {card.isCount ? (card.value || 0) : formatAmount(card.value)}
                </Typography>
              </Box>
            ))}
          </Box>
          {(summary.by_status || []).length > 0 && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: `repeat(${Math.min(summary.by_status.length, 4)}, 1fr)` }, gap: 1.5 }}>
              {summary.by_status.map((row) => (
                <Box
                  key={row.status}
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: STATUS_COLORS[row.status]?.bg || '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: 13, color: STATUS_COLORS[row.status]?.color || '#475569' }}>
                    {STATUS_LABELS[row.status] || row.status_label || row.status} ({row.order_count})
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 0.4 }}>
                    Total {formatAmount(row.total_amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#15803d' }}>
                    Collected {formatAmount(row.received_amount)}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: '#b91c1c', fontWeight: 700 }}>
                    Outstanding {formatAmount(row.balance_amount)}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Table */}
      {loading && orders.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Typography variant="h6" sx={{ color: '#94a3b8', mb: 1 }}>
            {isNotCreatedMode ? 'No clients without orders' : 'No orders found'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94a3b8' }}>
            {isNotCreatedMode
              ? 'All matching clients already have an order for the selected filters.'
              : 'Create a new order to get started'}
          </Typography>
        </Paper>
      ) : (
        <>
          {!isNotCreatedMode && selectedOrderIds.length > 0 && (
            <Paper sx={{ p: 1.5, mb: 1.5, borderRadius: 2.5, border: '1px solid #bfdbfe', bgcolor: '#eff6ff', boxShadow: 'none', display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1d4ed8' }}>
                {selectedOrderIds.length} selected
              </Typography>
              <FormControl size="small" sx={{ minWidth: 220, bgcolor: '#fff' }}>
                <InputLabel>Assign to staff</InputLabel>
                <Select
                  value={bulkAssignee}
                  label="Assign to staff"
                  onChange={(e) => setBulkAssignee(e.target.value)}
                >
                  {assigneeUsers.map((user) => (
                    <MenuItem key={user.id} value={String(user.id)}>
                      {user.username || user.email || user.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                disabled={!bulkAssignee || bulkAssigning}
                onClick={handleBulkAssign}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
              >
                {bulkAssigning ? 'Assigning...' : 'Assign selected'}
              </Button>
              <Button
                onClick={() => setSelectedOrderIds([])}
                disabled={bulkAssigning}
                sx={{ textTransform: 'none', color: '#475569' }}
              >
                Clear
              </Button>
            </Paper>
          )}
          <TableContainer component={Paper} sx={{ borderRadius: 2.5, border: '1px solid #e2e8f0', boxShadow: 'none', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 850 }}>
              <TableHead>
                <TableRow sx={{ '& th': { background: '#f8fafc', fontWeight: 700, color: '#475569', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, py: 1.2 } }}>
                  {!isNotCreatedMode && (
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={allPageSelected}
                        indeterminate={!allPageSelected && selectableIds.some((id) => selectedOrderIds.includes(id))}
                        onChange={togglePageSelected}
                        disabled={!selectableIds.length}
                      />
                    </TableCell>
                  )}
                  {LIST_COLUMNS.map((col) => (
                    <TableCell
                      key={col.key}
                      align={col.align || 'left'}
                      sortDirection={sortBy === col.key ? sortDir : false}
                      sx={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                    >
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDir : 'asc'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSort(col.key);
                        }}
                        hideSortIcon={false}
                        sx={{
                          fontWeight: 700,
                          fontSize: 11,
                          color: '#475569',
                          '& .MuiTableSortLabel-icon': { fontSize: 16, opacity: 0.7 },
                        }}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedOrders.map((order) => (
                  <TableRow
                    key={order.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      background: selectedOrderIds.includes(order.id) ? '#eff6ff' : undefined,
                      '&:hover': { background: '#f8fafc' },
                    }}
                    onClick={() => {
                      if (order.is_not_created || order.status === 'not_created') {
                        openCreateForCompany(order);
                        return;
                      }
                      navigate(`/billing/orders-v2/form?id=${order.id}`);
                    }}
                  >
                    {!isNotCreatedMode && (
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        {order.is_not_created || order.status === 'not_created' ? null : (
                          <Checkbox
                            size="small"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => toggleOrderSelected(order.id)}
                          />
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700, color: order.is_not_created ? '#94a3b8' : '#2563eb' }}>
                      {order.is_not_created ? '-' : order.order_number}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                      {order.is_not_created || order.status === 'not_created' ? (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>-</Typography>
                      ) : (
                        <TextField
                          type="date"
                          size="small"
                          value={order.collection_reminder_date || ''}
                          onChange={(e) => handleCollectionReminderChange(order, e.target.value)}
                          sx={{
                            width: 150,
                            '& .MuiInputBase-input': { py: 0.6, fontSize: 12, fontWeight: 600 },
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={order.academic_year_name || (isNotCreatedMode && currentSelectedYearObj?.name) || '-'}
                        size="small"
                        sx={{ height: 22, fontSize: 11, fontWeight: 600, bgcolor: '#f0fdf4', color: '#15803d', borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                        {order.company_name || '-'}
                      </Typography>
                      {order.company_code && (
                        <Typography variant="caption" sx={{ color: '#6366f1', fontWeight: 600, display: 'block' }}>
                          Code: {order.company_code}
                        </Typography>
                      )}
                      {!order.is_not_created && Array.isArray(order.opted_features) && order.opted_features.length > 0 && typeof order.opted_features[0] === 'object' && (
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {order.opted_features.map((item, i) => (
                            <Chip
                              key={i}
                              label={item.name || 'Service'}
                              size="small"
                              sx={{ height: 20, fontSize: 10, bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 600, borderRadius: 1 }}
                            />
                          ))}
                        </Box>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={order.is_not_created ? '0 items' : `${Array.isArray(order.opted_features) ? order.opted_features.length : 0} items`}
                        size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 600, bgcolor: '#f1f5f9', color: '#475569' }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatAmount(order.total_amount)}</TableCell>
                    <TableCell align="right" sx={{ color: '#16a34a', fontWeight: 700 }}>{formatAmount(order.received_amount)}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(order.balance_amount) > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                      {formatAmount(order.balance_amount)}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {order.is_not_created || order.status === 'not_created' ? (
                        <Chip
                          label={STATUS_LABELS[order.status] || order.status}
                          size="small"
                          sx={{
                            background: STATUS_COLORS[order.status]?.bg || '#f3f4f6',
                            color: STATUS_COLORS[order.status]?.color || '#6b7280',
                            fontWeight: 700,
                            fontSize: 11,
                          }}
                        />
                      ) : (
                        <Select
                          value={STATUS_OPTIONS.some((opt) => opt.value === order.status) ? order.status : 'draft'}
                          size="small"
                          disabled={updatingStatusId === order.id}
                          onChange={(e) => handleStatusChange(order, e.target.value)}
                          renderValue={(value) => STATUS_LABELS[value] || value}
                          sx={{
                            minWidth: 132,
                            height: 28,
                            borderRadius: 1.5,
                            fontWeight: 700,
                            fontSize: 11,
                            color: STATUS_COLORS[order.status]?.color || '#6b7280',
                            bgcolor: STATUS_COLORS[order.status]?.bg || '#f3f4f6',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { py: 0.4, pl: 1.2, pr: '28px !important' },
                          }}
                        >
                          {STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13, fontWeight: 600, color: STATUS_COLORS[opt.value]?.color }}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {order.is_not_created || order.status === 'not_created' ? (
                        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600 }}>Unassigned</Typography>
                      ) : (
                        <Select
                          value={order.assigned_to ? String(order.assigned_to) : ''}
                          size="small"
                          displayEmpty
                          disabled={updatingAssigneeId === order.id}
                          onChange={(e) => handleAssigneeChange(order, e.target.value)}
                          renderValue={(value) => {
                            if (!value) return 'Unassigned';
                            const picked = assigneeUsers.find((user) => String(user.id) === String(value));
                            return picked?.username || order.assigned_to_name || 'Assigned';
                          }}
                          sx={{
                            minWidth: 140,
                            height: 28,
                            borderRadius: 1.5,
                            fontWeight: 700,
                            fontSize: 11,
                            color: order.assigned_to ? '#1d4ed8' : '#64748b',
                            bgcolor: order.assigned_to ? '#eff6ff' : '#f8fafc',
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiSelect-select': { py: 0.4, pl: 1.2, pr: '28px !important' },
                          }}
                        >
                          <MenuItem value="" sx={{ fontSize: 13, color: '#64748b' }}>Unassigned</MenuItem>
                          {order.assigned_to && !assigneeUsers.some((user) => String(user.id) === String(order.assigned_to)) && (
                            <MenuItem value={String(order.assigned_to)} sx={{ fontSize: 13, fontWeight: 600 }}>
                              {order.assigned_to_name || 'Assigned user'}
                            </MenuItem>
                          )}
                          {assigneeUsers.map((user) => (
                            <MenuItem key={user.id} value={String(user.id)} sx={{ fontSize: 13, fontWeight: 600 }}>
                              {user.username || user.email || user.id}
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {order.is_not_created || order.status === 'not_created' ? (
                        <Tooltip title="Create Order">
                          <IconButton
                            size="small"
                            onClick={() => openCreateForCompany(order)}
                            sx={{ color: '#059669' }}
                          >
                            <AddCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <Tooltip title="Edit Order">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/billing/orders-v2/form?id=${order.id}`)}
                              sx={{ color: '#2563eb' }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Print Proforma">
                            <IconButton
                              size="small"
                              onClick={() => handlePrintInvoice(order)}
                              sx={{ color: '#0284c7' }}
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Add Amount">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/billing/orders-v2/form?id=${order.id}&add_payment=1`)}
                              sx={{ color: '#059669' }}
                            >
                              <AddCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Order">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteOrder(order)}
                              sx={{ color: '#dc2626' }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={1.5}
            sx={{ mt: 2, px: 0.5 }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
              {(() => {
                const start = total === 0 ? 0 : (page - 1) * limit + 1;
                const end = Math.min(page * limit, total);
                const noun = isNotCreatedMode
                  ? (total === 1 ? 'client' : 'clients')
                  : (total === 1 ? 'order' : 'orders');
                return `Showing ${start}-${end} of ${total} ${noun}`;
              })()}
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, val) => setPage(val)}
              color="primary"
              shape="rounded"
            />
          </Stack>
        </>
      )}
    </Box>
  );
}

export default OrdersV2List;
